import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { PageBanner } from "@/components/site/PageBanner";
import { PropertyCard } from "@/components/site/PropertyCard";
import { api } from "@/lib/api";
import { findCommunityContent } from "@/lib/communities";
import {
  findDeveloper,
  propertyMatchesDeveloper,
  type DeveloperContent,
} from "@/lib/developers";
import { DEVELOPER_LOGOS } from "@/lib/developer-logos";
import { getPropertyCoverOffsetMap } from "@/lib/property-images";
import { absoluteUrl, breadcrumbLd } from "@/lib/structured-data";
import { useJsonLd } from "@/hooks/useJsonLd";
import { useLiveProperties } from "@/hooks/useLiveProperties";
import { useSeo } from "@/hooks/useSeo";
import type { Property } from "@/types/property";

export function DeveloperPage() {
  const { slug = "" } = useParams();
  const developer = findDeveloper(slug);

  if (!developer) {
    return <Navigate to="/developers" replace />;
  }

  // Stable key remounts the view between developer pages so in-flight
  // requests and scroll state reset, mirroring CommunityPage.
  return <DeveloperView key={developer.slug} developer={developer} />;
}

function DeveloperView({ developer }: { developer: DeveloperContent }) {
  const { version } = useLiveProperties();
  const [items, setItems] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const logo = DEVELOPER_LOGOS[developer.slug];

  useSeo({
    title: developer.seoTitle,
    description: developer.seoDescription,
    ogImage: absoluteUrl(developer.cover),
  });

  useJsonLd(
    "developer-breadcrumb",
    breadcrumbLd([
      { name: "Home", path: "/" },
      { name: "Developers", path: "/developers" },
      { name: developer.name, path: `/developers/${developer.slug}` },
    ]),
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // The feed has no developer field, so pull the catalogue and match
    // against the curated keyword rules client-side.
    api
      .listProperties({ pageSize: 500, sort: "newest" })
      .then(
        (data) =>
          !cancelled &&
          setItems(data.items.filter((p) => propertyMatchesDeveloper(p, developer))),
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [developer, version]);

  const coverOffsets = useMemo(() => getPropertyCoverOffsetMap(items), [items]);
  const communities = developer.communitySlugs
    .map((slug) => findCommunityContent(slug))
    .filter((c): c is NonNullable<typeof c> => Boolean(c));

  return (
    <>
      <PageBanner image={developer.cover} alt={developer.coverAlt}>
        <Link
          to="/developers"
          className="inline-flex items-center gap-2 text-[11.5px] uppercase tracking-[0.22em] text-[var(--text-on-dark-soft)] hover:text-[var(--cream)]"
        >
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M12 7H2m4-4-4 4 4 4" stroke="currentColor" strokeWidth="1.4" />
          </svg>
          All Developers
        </Link>

        {logo && (
          <span className="mt-6 inline-flex items-center bg-white px-4 py-2.5">
            <img
              src={logo.src}
              alt={`${developer.name} logo`}
              className={`w-auto max-w-[150px] object-contain ${logo.className}`}
            />
          </span>
        )}

        <h1 className="mt-5 display-xl max-w-[16ch] text-[var(--text-on-dark)]" translate="no">
          {developer.name}.
        </h1>
        <p className="mt-5 max-w-[58ch] text-[14.5px] leading-[1.65] text-[var(--text-on-dark-soft)] sm:text-[15.5px] sm:leading-[1.7]">
          {developer.blurb}
        </p>

        {!loading && (
          <p className="mt-7 flex flex-wrap items-baseline gap-x-2.5 gap-y-1 border-t border-[var(--line)] pt-5 text-[13px] uppercase tracking-[0.16em] text-[var(--text-muted)]">
            <span className="font-display text-[20px] tracking-normal text-[var(--gold-bright)] tabular-nums">
              {String(items.length).padStart(2, "0")}
            </span>
            Live {items.length === 1 ? "Listing" : "Listings"}
          </p>
        )}
      </PageBanner>

      {/* About the developer */}
      <section className="border-b border-[var(--line)]">
        <div className="container-wide grid gap-10 py-12 sm:py-14 md:grid-cols-12 md:gap-14 md:py-16 short:py-8">
          <div className="md:col-span-7">
            <p className="eyebrow">About {developer.name}</p>
            {developer.paragraphs.map((paragraph) => (
              <p
                key={paragraph.slice(0, 32)}
                className="mt-5 max-w-[64ch] text-[15.5px] leading-[1.75] text-[var(--text-soft)] sm:text-[16.5px]"
              >
                {paragraph}
              </p>
            ))}
          </div>
          {communities.length > 0 && (
            <div className="md:col-span-5">
              <p className="eyebrow">Where They Build</p>
              <ul className="mt-5 flex flex-wrap gap-2">
                {communities.map((community) => (
                  <li key={community.slug}>
                    <Link
                      to={`/areas/${community.slug}`}
                      className="inline-flex min-h-[44px] items-center border border-[var(--line-strong)] px-4 text-[12px] uppercase tracking-[0.16em] text-[var(--text-soft)] transition-colors hover:border-[var(--gold-bright)] hover:text-[var(--gold-bright)]"
                    >
                      {community.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* Live properties by this developer */}
      <section className="container-wide py-12 sm:py-14 md:py-20 short:py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[var(--line)] pb-5">
          <div>
            <p className="eyebrow">Live Availability</p>
            <h2
              className="mt-3 max-w-[20ch] font-display text-[28px] font-semibold leading-[1.05] tracking-[-0.02em] text-[var(--text)] sm:text-[34px] md:text-[40px]"
              style={{ fontVariationSettings: '"opsz" 72' }}
            >
              Properties by {developer.name}.
            </h2>
          </div>
          <Link to="/properties" className="link-arrow">
            See All Listings
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M2 7h10m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </Link>
        </div>

        <div className="mt-8 grid gap-x-6 gap-y-10 sm:mt-10 md:grid-cols-2 lg:grid-cols-3">
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex flex-col gap-4">
                <div className="aspect-[16/10] animate-pulse bg-[var(--surface-2)]" />
                <div className="h-3 w-1/3 animate-pulse bg-[var(--surface-2)]" />
                <div className="h-5 w-2/3 animate-pulse bg-[var(--surface-2)]" />
              </div>
            ))}
          {!loading &&
            items.map((property) => (
              <PropertyCard
                key={property.id}
                property={property}
                coverOffset={coverOffsets.get(property.id) ?? 0}
              />
            ))}
        </div>

        {!loading && items.length === 0 && (
          <div className="mt-8 border border-dashed border-[var(--line-strong)] p-10 text-center sm:mt-10">
            <p
              className="font-display text-[22px] text-[var(--text)] sm:text-[26px]"
              style={{ fontVariationSettings: '"opsz" 36' }}
            >
              No live {developer.name} listings right now.
            </p>
            <p className="mx-auto mt-3 max-w-[52ch] text-[14px] text-[var(--text-soft)]">
              Inventory moves fast — we usually know what's coming to market
              before it's public. Tell us what you're looking for.
            </p>
            <div className="mt-6 flex justify-center">
              <Link to="/contact" className="btn btn-primary btn-block-sm">
                {developer.ctaLabel}
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                  <path d="M2 7h10m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.4" />
                </svg>
              </Link>
            </div>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="mt-10 flex justify-center border-t border-[var(--line)] pt-8 sm:mt-12">
            <Link to="/contact" className="btn btn-primary btn-block-sm">
              {developer.ctaLabel}
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                <path d="M2 7h10m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.4" />
              </svg>
            </Link>
          </div>
        )}
      </section>
    </>
  );
}
